import L from 'leaflet'
import {
  loadEsriLeaflet,
  checkYearService,
  googleHybridProvider,
} from '../../MapWithDrawing/Measure/mapUtils'

let latestMaricopaProviderPromise = null

const GIO_BASE = 'https://gis.maricopa.gov/imagery/rest/services'
const GIO_FALLBACKS = [
  { year: 2023, name: 'Aerial2023Sep2023OctOrtho' },
  { year: 2022, name: 'Aerial2022Sep2022OctOrtho' },
  { year: 2021, name: 'Aerial2021Sep2021NovOrtho' },
]

const fetchJsonWithTimeout = async (url, timeoutMs) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const resp = await fetch(url, { signal: controller.signal })
    if (!resp.ok) return null
    return await resp.json()
  } catch (_) {
    return null
  } finally {
    clearTimeout(timeoutId)
  }
}

const probeHeadWithTimeout = async (url, timeoutMs) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const resp = await fetch(url, { method: 'HEAD', signal: controller.signal })
    return resp.ok
  } catch (_) {
    return false
  } finally {
    clearTimeout(timeoutId)
  }
}

const loadLatestMaricopaProviderUncached = async () => {
  await loadEsriLeaflet()

  const currentYear = new Date().getFullYear()
  const years = []
  for (let y = currentYear + 1; y >= 2017; y -= 1) years.push(y)

  const assessorResults = await Promise.all(years.map((year) => checkYearService(year)))
  const validAssessorProviders = assessorResults.filter(Boolean)
  const foundYears = new Set(validAssessorProviders.map((provider) => provider.year))

  const gioResults = await Promise.all(
    GIO_FALLBACKS
      .filter((svc) => !foundYears.has(svc.year))
      .map(async (svc) => {
        const serviceUrl = `${GIO_BASE}/${svc.name}/MapServer`
        const tileOk = await probeHeadWithTimeout(`${serviceUrl}/tile/8/26304/12358`, 5000)
        if (!tileOk) return null

        const data = await fetchJsonWithTimeout(`${serviceUrl}?f=json`, 8000)
        if (!data?.tileInfo?.lods?.length) return null

        return {
          year: svc.year,
          name: `Maricopa County ${svc.year}`,
          displayName: `Maricopa County (${svc.year})`,
          url: `${serviceUrl}/tile/{z}/{y}/{x}`,
          serviceUrl,
          attribution: `© Maricopa County (${svc.year})`,
          maxZoom: data.tileInfo.lods[data.tileInfo.lods.length - 1].level,
          minZoom: data.tileInfo.lods[0].level,
          isTiledService: true,
          isExportService: false,
          tilesVerified: true,
          group: 'maricopa',
        }
      })
  )

  const allValidProviders = [...validAssessorProviders, ...gioResults.filter(Boolean)]
    .sort((a, b) => (b.year || 0) - (a.year || 0))

  return allValidProviders[0] || googleHybridProvider
}

export const getLatestMaricopaProvider = () => {
  if (!latestMaricopaProviderPromise) {
    latestMaricopaProviderPromise = loadLatestMaricopaProviderUncached().catch((err) => {
      latestMaricopaProviderPromise = null
      throw err
    })
  }
  return latestMaricopaProviderPromise
}

export const applyBasemapProvider = ({ map, tileLayerRef, provider }) => {
  if (!map || !provider) return null

  if (tileLayerRef.current) {
    try { map.removeLayer(tileLayerRef.current) } catch (_) {}
    tileLayerRef.current = null
  }

  let layer
  if (provider.isExportService && window.L?.esri?.dynamicMapLayer) {
    layer = window.L.esri.dynamicMapLayer({
      url: provider.url,
      maxZoom: 28,
    })
  } else {
    layer = L.tileLayer(provider.url, {
      attribution: provider.attribution || '',
      maxNativeZoom: Math.min(provider.maxZoom || 20, 21),
      maxZoom: 28,
      errorTileUrl: '',
    })
  }

  layer.addTo(map)
  if (layer.setZIndex) layer.setZIndex(0)
  tileLayerRef.current = layer
  return layer
}

export { googleHybridProvider }
